import React from 'react';
import { Link } from 'react-router-dom';
import { FileSpreadsheet, ArrowRight, Lock } from 'lucide-react';

/**
 * Empty state shown on Fraud / PCC / DLC pages when no master file has been
 * (mock-)uploaded by the Admin yet. Enforces HMSI's "no auto-creation" rule:
 * tasks must come strictly from an uploaded Excel master, never auto-generated.
 *
 * Props:
 *   moduleLabel  – e.g. "Fraud Prevention"
 *   moduleKey    – fraud | pcc | dlc (used for testid)
 *   accent       – hex colour for the icon/badge tint
 *   isAdmin      – true if current user is admin (shows direct CTA)
 */
export default function MasterEmptyState({ moduleLabel, moduleKey, accent = '#0EA5E9', isAdmin = false }) {
  return (
    <div
      className="bg-white rounded-xl border border-slate-200 p-8"
      data-testid={`master-empty-${moduleKey}`}
    >
      <div className="flex items-start gap-5">
        <div
          className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: accent + '15', color: accent }}
        >
          <FileSpreadsheet className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5">
            <h3 className="text-base font-bold text-slate-900">No master file uploaded yet</h3>
            <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 text-[9px] font-bold uppercase tracking-wider">
              <Lock className="w-2.5 h-2.5" /> No auto-creation
            </span>
          </div>
          <p className="text-sm text-slate-600 leading-relaxed max-w-2xl">
            The {moduleLabel} checklist is populated <b>strictly from the official HMSI Excel master</b>.
            Until an Admin uploads it from <span className="font-semibold">Master Configuration</span>, no questions
            will appear here. This enforces HGS&apos;s rule that the system must never auto-generate checklist content.
          </p>

          <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2 max-w-2xl">
            <Step number="1" text="Admin uploads the official Excel master" />
            <Step number="2" text="System parses sections + sheets" />
            <Step number="3" text="Department Heads see their tasks" />
          </div>

          {isAdmin && (
            <Link
              to="/admin/masters"
              className="mt-5 inline-flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg shadow-sm transition hover:opacity-90"
              style={{ backgroundColor: accent }}
              data-testid={`empty-cta-upload-${moduleKey}`}
            >
              Open Master Configuration <ArrowRight className="w-4 h-4" />
            </Link>
          )}

          {!isAdmin && (
            <div className="mt-5 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-50 border border-amber-200 text-[11px] text-amber-900">
              <Lock className="w-3 h-3" />
              <span>
                Please ask the <b>GRC Admin</b> to upload the {moduleLabel} master file for this cycle.
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Step({ number, text }) {
  return (
    <div className="flex items-start gap-2 p-2.5 rounded-lg bg-slate-50 border border-slate-100">
      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-slate-900 text-white text-[10px] font-bold flex items-center justify-center">
        {number}
      </span>
      <span className="text-[11px] font-medium text-slate-700 leading-snug">{text}</span>
    </div>
  );
}
